import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Header from './components/Header';
import VideoUploader from './components/VideoUploader';
import VideoGrid from './components/VideoGrid';
import VideoPlayer from './components/VideoPlayer';
import MusicPlayer from './components/MusicPlayer';
import PlaylistList from './components/PlaylistList';
import PlaylistDetail from './components/PlaylistDetail';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: '#000000',
      paper: '#121212',
    },
  },
});

// Account interface
export interface Account {
  userId: string;
  username: string;
  token: string;
  isActive: boolean;
}

const HeaderWithLocation: React.FC<{
  isAuthenticated: boolean;
  currentAccount: Account | null;
  allAccounts: Account[];
  onLogin: (token: string, userId: string, username: string) => void;
  onLogout: () => void;
  onLogoutAll: () => void;
  onSwitchAccount: (userId: string) => void;
  onSearch?: (results: any[]) => void;
}> = (props) => {
  const location = useLocation();
  const isPlayerView = location.pathname.includes('/video/') || location.pathname.includes('/music/');
  const isMusicView = location.pathname.includes('/music/');
  const prevPathnameRef = React.useRef(location.pathname);
  
  // Scroll to top on route change, but skip when navigating between music tracks
  useEffect(() => {
    const prev = prevPathnameRef.current;
    prevPathnameRef.current = location.pathname;
    const isMusicToMusic = prev.startsWith('/music/') && location.pathname.startsWith('/music/');
    if (!isMusicToMusic) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname]);

  return <Header {...props} isPlayerView={isPlayerView} isMusicView={isMusicView} />;
};

const App: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currentAccount, setCurrentAccount] = useState<Account | null>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);

  // Load accounts from localStorage on mount
  useEffect(() => {
    const storedAccounts = localStorage.getItem("accounts");
    if (storedAccounts) {
      const parsedAccounts: Account[] = JSON.parse(storedAccounts);
      setAccounts(parsedAccounts);
      
      // Find and set the active account
      const activeAccount = parsedAccounts.find(acc => acc.isActive);
      if (activeAccount) {
        setCurrentAccount(activeAccount);
        // Set the JWT token for API calls
        localStorage.setItem("jwt", activeAccount.token);
      }
    }
  }, []);

  // Save accounts to localStorage whenever they change
  useEffect(() => {
    if (accounts.length > 0) {
      localStorage.setItem("accounts", JSON.stringify(accounts));
    } else {
      localStorage.removeItem("accounts");
    }
  }, [accounts]);

  const handleLogin = (token: string, userId: string, username: string) => {
    // Check if account already exists
    const existingAccountIndex = accounts.findIndex(acc => acc.userId === userId);
    
    let updatedAccounts: Account[];
    
    if (existingAccountIndex !== -1) {
      // Update existing account
      updatedAccounts = accounts.map((acc, index) => ({
        ...acc,
        isActive: index === existingAccountIndex,
        token: index === existingAccountIndex ? token : acc.token
      }));
    } else {
      // Add new account and set as active
      const newAccount: Account = {
        userId,
        username,
        token,
        isActive: true
      };
      
      // Set all existing accounts to inactive
      updatedAccounts = [
        ...accounts.map(acc => ({ ...acc, isActive: false })),
        newAccount
      ];
    }
    
    setAccounts(updatedAccounts);
    const activeAccount = updatedAccounts.find(acc => acc.isActive)!;
    setCurrentAccount(activeAccount);
    localStorage.setItem("jwt", activeAccount.token);
    
    // Trigger refresh
    window.location.reload();
  };

  const handleSwitchAccount = (userId: string) => {
    const updatedAccounts = accounts.map(acc => ({
      ...acc,
      isActive: acc.userId === userId
    }));
    
    setAccounts(updatedAccounts);
    const activeAccount = updatedAccounts.find(acc => acc.userId === userId)!;
    setCurrentAccount(activeAccount);
    localStorage.setItem("jwt", activeAccount.token);
    
    // Trigger refresh
    window.location.reload();
  };

  const handleLogout = () => {
    if (!currentAccount) return;
    
    // Remove current account from the list
    const updatedAccounts = accounts.filter(acc => acc.userId !== currentAccount.userId);
    
    if (updatedAccounts.length > 0) {
      // If there are other accounts, switch to the first one
      updatedAccounts[0].isActive = true;
      setAccounts(updatedAccounts);
      setCurrentAccount(updatedAccounts[0]);
      localStorage.setItem("jwt", updatedAccounts[0].token);
    } else {
      // No accounts left
      setAccounts([]);
      setCurrentAccount(null);
      localStorage.removeItem("jwt");
    }
    
    window.location.reload();
  };

  const handleLogoutAll = () => {
    setAccounts([]);
    setCurrentAccount(null);
    localStorage.removeItem("jwt");
    localStorage.removeItem("accounts");
    window.location.reload();
  };

  const handleVideoListChange = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const handleSearch = useCallback((results: any[]) => {
    setSearchResults(results);
  }, []);

  const isAuthenticated = currentAccount !== null;

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Router>
        <HeaderWithLocation 
          isAuthenticated={isAuthenticated}
          currentAccount={currentAccount}
          allAccounts={accounts}
          onLogin={handleLogin}
          onLogout={handleLogout}
          onLogoutAll={handleLogoutAll}
          onSwitchAccount={handleSwitchAccount}
          onSearch={handleSearch}
        />
        <Routes>
          <Route 
            path="/" 
            element={
              <>
                <VideoUploader 
                  onVideoListChange={handleVideoListChange} 
                />
                <VideoGrid refreshTrigger={refreshTrigger} searchResults={searchResults} />
              </>
            } 
          />
          <Route path="/video/:vid" element={<VideoPlayer searchResults={searchResults} />} />
          <Route path="/music/:vid" element={<MusicPlayer searchResults={searchResults} />} />
          <Route path="/playlists" element={<PlaylistList />} />
          <Route path="/playlists/:pid" element={<PlaylistDetail />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
};

export default App;